import React, { useState, useCallback, useEffect } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { validateUrl } from '../utils/security';
import { debounce } from '../services/api';
import { useTranslation } from 'react-i18next';
// import { PlusIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

// Esta función ya no es necesaria, usamos la de security.js
// const isValidUrl = (url) => {
//   try {
//     new URL(url);
//     return true;
//   } catch (e) {
//     return false;
//   }
// };

const URLInput = ({ onSubmit, isLoading = false }) => {
  const [url, setUrl] = useState('');
  const [invalidUrlMessage, setInvalidUrlMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const { t } = useTranslation();

  // Create debounced validation function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidation = useCallback(
    debounce((inputUrl) => {
      const trimmedUrl = inputUrl.trim();
      if (!trimmedUrl) {
        setIsValid(false);
        setInvalidUrlMessage('');
        return;
      }

      const valid = validateUrl(trimmedUrl);
      setIsValid(valid);
      
      if (!valid) {
        try {
          // Si podemos crear la URL pero no pasa la validación de seguridad
          new URL(trimmedUrl);
          setInvalidUrlMessage(t('validation.urlNotAllowed'));
        } catch (e) {
          // Si ni siquiera es una URL válida
          setInvalidUrlMessage(t('validation.invalidUrl'));
        }
      } else {
        setInvalidUrlMessage('');
      }
    }, 300),
    [t]
  );

  // Run validation when URL changes
  useEffect(() => {
    debouncedValidation(url);
  }, [url, debouncedValidation]);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Formulario enviado con URL:', url);
    
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
      setInvalidUrlMessage(t('validation.required'));
      return;
    }
    
    if (!isValid) {
      return;
    }
    
    console.log('URL válida, llamando a onSubmit con:', trimmedUrl);
    onSubmit(trimmedUrl);
    // Limpiar el campo de texto después de enviar
    setUrl('');
  };

  const isDisabled = isLoading || !url.trim() || !isValid;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-[750px] mx-auto px-4">
      <div className={`relative flex items-center bg-white rounded-full border ${invalidUrlMessage ? 'border-red-500' : 'border-gray-300'} shadow-[0_2px_10px_rgba(0,0,0,0.10)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-200 overflow-hidden`}>
        <input
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            // Limpiar el mensaje de error cuando el usuario comienza a escribir de nuevo
            if (invalidUrlMessage) {
              setInvalidUrlMessage('');
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={t('homepage.placeholder')}
          required
          className="w-full py-3.5 pl-5 pr-14 bg-transparent text-gray-700 text-base sm:text-sm focus:outline-none"
          style={{ fontSize: '16px' }}
          disabled={isLoading}
          aria-invalid={invalidUrlMessage ? 'true' : 'false'}
          inputMode="url"
          autoComplete="off"
        />

        <div className="absolute right-2">
          <button
            type="submit"
            disabled={isDisabled}
            className={`p-2 rounded-full ${
              isDisabled
                ? 'text-gray-300 cursor-not-allowed' 
                : 'bg-black text-white hover:bg-gray-800 transition-colors'
            }`}
            aria-label={t('actions.submit')}
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {invalidUrlMessage && (
        <div className="text-xs text-red-500 mt-1 ml-2">
          {invalidUrlMessage}
        </div>
      )}
      
      <div className="text-xs text-center text-gray-500 mt-2">
        {!isLoading && !invalidUrlMessage && (
          <span>{t('app.inputPrompt')}</span>
        )}
        {isLoading && (
          <span className="text-blue-500">{t('homepage.processing')}</span>
        )}
      </div>
    </form>
  );
};

export default URLInput; 